/**
 * Button Component
 *
 * Reusable button with variants, sizes, and loading state.
 */

import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button style variant */
  variant?: 'primary' | 'secondary' | 'danger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show loading spinner and disable interaction */
  loading?: boolean;
  /** Loading text (defaults to children) */
  loadingText?: string;
  /** Button content */
  children: ReactNode;
}

// Graphite hover LIGHTENS and pressed goes darker: a rack button lights up,
// it does not dim. Outlined buttons invert to a dark fill on hover.
const variantClasses = {
  primary: `
    text-white bg-rack hover:bg-rack-hairline active:bg-rack-deep
    focus:ring-rack border-transparent
  `,
  secondary: `
    text-ink bg-white hover:bg-rack hover:text-white hover:border-rack
    active:bg-rack-deep active:border-rack-deep
    focus:ring-rack border-hairline
  `,
  danger: `
    text-white bg-program hover:bg-program-dark active:bg-program-dark
    focus:ring-program border-transparent
  `,
};

const sizeClasses = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-2.5 text-base',
};

/**
 * Button renders a styled button with consistent appearance.
 *
 * Usage:
 * ```tsx
 * <Button variant="primary" loading={isSubmitting}>
 *   Save Changes
 * </Button>
 * ```
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      children,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const baseClasses = `
      inline-flex items-center justify-center font-medium rounded-md border
      focus:outline-none focus:ring-2 focus:ring-offset-2
      transition-colors
      disabled:opacity-50 disabled:cursor-not-allowed
    `.trim();

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        {...props}
      >
        {loading ? (
          <>
            <Spinner className="h-4 w-4 mr-2" />
            {loadingText || children}
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;

// =============================================================================
// Spinner Icon
// =============================================================================

function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
